'use client';

import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { FormattedMessage, useIntl } from 'react-intl';

import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text3, text5 } from '@osmosis/tailwind-ui/styles/text';
import { usePagesContext } from '@osmosis/nextjs-components/context/PagesProvider';
import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';
import { useLocalStorage } from '@osmosis/shared/hooks';

import { fade } from '../../styles/animation';
import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import { useAIPanelContext } from '../../context/AIPanelContext';
import useOsmosisAI from '../../hooks/useOsmosisAI';
import { track } from '../../lib/util/tracking';
import {
  ANALYTICS_LABELS,
  EXAMPLE_PROMPTS,
  PromptDefinition,
  PromptId,
} from '../../lib/util/examplePrompts';
import { useLearnData } from '../../context/LearnDataProvider';

// Pause overlay surfaces the shared set without the tutor prompt, in the shared order.
const PAUSE_PROMPT_IDS: PromptId[] = ['questions', 'flashcards', 'summarize'];
const PAUSE_PROMPTS = PAUSE_PROMPT_IDS.map(
  (id) => EXAMPLE_PROMPTS.find((p) => p.id === id)!,
).filter(Boolean) as PromptDefinition[];

// Frosted-glass pill shared by dismiss + prompt buttons.
const pillClass = cn(
  'rounded-full border border-white/60 bg-white/95 backdrop-blur-sm shadow-md',
  'transition-transform duration-150 hover:scale-[1.012] active:scale-[1.012] hover:text-black active:text-black hover:bg-white/95 active:bg-white/95',
);

/**
 * Dismissible overlay shown over the Learn page video when the user pauses,
 * offering contextual Osmosis AI prompts. Non-fullscreen, medium+ screens only.
 */
const VideoPauseOverlay = () => {
  const { formatMessage } = useIntl();
  const { playing, isFullscreen, isStarted } = useVideoPlayer();
  const { topic, sendPromptOpeningPanel } = useAIPanelContext();
  const { showAIButton, shouldShowLockout } = useOsmosisAI();
  const { md } = useContentSize();
  const { pathname } = usePagesContext();
  const { learnData } = useLearnData();

  // Testing flag: when true in localStorage, the pause overlay is suppressed.
  const [disableOverlay] = useLocalStorage('disableVideoPauseOverlay', false);

  const [dismissed, setDismissed] = useState(false);

  // True once playing has been observed — prevents a spurious visible flash
  // when isStarted flips to true before playing has ever been true.
  const hasEverPlayed = useRef(false);

  // First prompt button — auto-focused when the overlay becomes visible.
  const firstPromptRef = useRef<HTMLButtonElement>(null);

  // Element focused before the overlay opened — focus is restored here on close.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Tracks the previous `visible` value so we can detect the open → close edge.
  const wasVisibleRef = useRef(false);

  const visible =
    hasEverPlayed.current &&
    !playing &&
    isStarted &&
    !isFullscreen &&
    md &&
    showAIButton &&
    !shouldShowLockout &&
    !!topic &&
    !dismissed &&
    !disableOverlay;

  const learnPageName = learnData?.name;
  const learnNodeID = learnData?.learnNodeID;

  useEffect(() => {
    if (playing) {
      hasEverPlayed.current = true;
    }
  }, [playing]);

  // Reset the dismissal when the video changes, so it stays scoped to the
  // current video even if this overlay persists across videos (mirrors
  // VideoContainer resetting its per-video state on mediaID change).
  useEffect(() => {
    setDismissed(false);
  }, [learnNodeID]);

  useEffect(() => {
    if (visible) {
      track(
        'osmosis-ai-pause-prompt-shown',
        { page: pathname, learnPageName },
        ['keen', 'pendo'],
      );
    }
  }, [visible, pathname, learnPageName]);

  // Restores focus to wherever it was before the overlay opened, falling back
  // to the player's play/pause button for keyboard users.
  const restoreFocus = () => {
    const previous = previouslyFocusedRef.current;
    previouslyFocusedRef.current = null;
    if (previous && previous.isConnected) {
      previous.focus();
      return;
    }
    const playButton = document.querySelector<HTMLButtonElement>(
      '[data-testid="play-pause-toggle-button"]',
    );
    playButton?.focus();
  };

  useEffect(() => {
    if (visible) {
      // Remember the element focused before we move focus into the overlay,
      // so it can be restored when the overlay closes.
      previouslyFocusedRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      firstPromptRef.current?.focus();
      wasVisibleRef.current = true;
    } else if (wasVisibleRef.current) {
      // Overlay just closed — via dismiss or by resuming playback. Return
      // focus to wherever it was before the overlay opened.
      wasVisibleRef.current = false;
      restoreFocus();
    }
  }, [visible]);

  const onPromptClick = (def: PromptDefinition) => {
    track(
      'osmosis-ai-pause-prompt-clicked',
      { page: pathname, learnPageName, prompt: ANALYTICS_LABELS[def.id] },
      ['keen', 'pendo'],
    );
    sendPromptOpeningPanel(formatMessage(def.prompt), def.mode);
  };

  const onDismiss = () => {
    track(
      'osmosis-ai-pause-prompt-dismissed',
      { page: pathname, learnPageName },
      ['keen', 'pendo'],
    );
    // Triggers the close edge in the effect above, which restores focus.
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          {...fade()}
          data-testid="video-pause-overlay"
          className="absolute z-10 left-0 top-0 flex w-full flex-col items-center justify-end bg-black/60 pb-5 h-[calc(100%-70px)]"
        >
          <Flex className="w-[88%] max-w-xl flex-col gap-3">
            <Flex className="w-full justify-end">
              <Button
                variant="transparent"
                onClick={onDismiss}
                data-testid="video-pause-overlay-dismiss"
                className={cn(pillClass, 'group/dismiss px-5 py-3')}
              >
                <P
                  className={cn(
                    '!text-sm font-semibold group-hover/dismiss:text-black group-active/dismiss:text-black',
                    text3,
                  )}
                >
                  <FormattedMessage
                    id="video-pause-overlay.dismiss"
                    defaultMessage="Dismiss"
                  />
                </P>
              </Button>
            </Flex>

            {PAUSE_PROMPTS.map((def, index) => (
              <Button
                key={def.id}
                ref={index === 0 ? firstPromptRef : undefined}
                variant="transparent"
                className={cn(
                  pillClass,
                  'w-full justify-start px-5 py-3 text-left',
                )}
                onClick={() => onPromptClick(def)}
                data-testid={`video-pause-prompt-${def.id}`}
              >
                <P className={cn('!text-sm font-semibold', text5)}>
                  <FormattedMessage {...def.label} />
                </P>
              </Button>
            ))}
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoPauseOverlay;
